import { type FC, ReactNode, useState } from 'react';

import { Box } from '../Box';
import { Button } from '../Button';
import { type FeatureItem, FeatureListItem } from '../FeatureList';
import { ProgressBar } from '../ProgressBar';
import { type TimeLeft, Timer } from '../Timer';
import { Heading, Text } from '../Typography';
import { UserCard } from '../UserCard';
import ProfileSubtitle from './components/ProfileSubtitle';
import { hasTextContent } from './LandownerProfile.helpers';

export type EarlyAccessTimerConfig = {
  /**
   * Unix timestamp (in seconds) for when early access expires
   */
  expirationTimestamp: number;
  /**
   * Small label text (displayed in xs size)
   * e.g., "Early Access is available for LandTrust+ members only."
   */
  labelText: ReactNode;
  /**
   * Description text (displayed in sm size)
   * e.g., "You'll be able to message Donald in:"
   */
  descriptionText: ReactNode;
  /**
   * Total duration in seconds (for calculating progress)
   */
  totalDuration: number;
};

export type LandownerProfileProps = {
  /**
   * Heading text for the profile
   */
  heading: string;
  /**
   * Avatar image URL for the landowner
   */
  avatarSrc?: string;
  /**
   * Name of the landowner
   */
  name: string;
  /**
   * ISO date string when hosting started.
   * If provided, will calculate and display years and months.
   */
  hostingSince?: string;
  /**
   * Optional feature item for reviews display
   */
  featureReviewItem?: FeatureItem;
  /**
   * Whether the landowner is verified
   */
  isVerified?: boolean;
  /**
   * Bio text content
   */
  bio?: string;
  /**
   * Response rate percentage (e.g., "100%")
   */
  responseRate?: string;
  /**
   * Response time text (e.g., "Within 6 Hours")
   */
  responseTime?: string;
  /**
   * Message button text
   */
  messageButtonText?: string;
  /**
   * Message button click handler
   */
  onMessageClick?: () => void;
  /**
   * Whether the message button is disabled
   */
  isMessageDisabled?: boolean;
  /**
   * Early access timer configuration.
   * When provided, shows a progress bar and countdown timer below the message button.
   */
  earlyAccessTimer?: EarlyAccessTimerConfig;
  /**
   * Callback fired when early access timer updates
   */
  onEarlyAccessTimeUpdate?: (timeLeft: TimeLeft) => void;
  /**
   * Callback fired when early access timer expires
   */
  onEarlyAccessExpire?: () => void;
  /**
   * Additional CSS class names
   */
  className?: string;
};

const LandownerProfile: FC<LandownerProfileProps> = ({
  heading,
  avatarSrc,
  name,
  hostingSince,
  featureReviewItem,
  isVerified = false,
  bio,
  responseRate,
  responseTime,
  messageButtonText = 'Message',
  onMessageClick,
  isMessageDisabled = false,
  earlyAccessTimer,
  onEarlyAccessTimeUpdate,
  onEarlyAccessExpire,
  className,
}) => {
  const calculateProgress = (timeLeft: TimeLeft): number => {
    if (!earlyAccessTimer) return 0;
    const { totalDuration } = earlyAccessTimer;
    if (totalDuration <= 0) return 100;
    const progressPercentage =
      ((totalDuration - timeLeft.totalSeconds) / totalDuration) * 100;
    return Math.min(100, Math.max(0, progressPercentage));
  };

  const [progress, setProgress] = useState(0);

  const handleTimeUpdate = (timeLeft: TimeLeft) => {
    setProgress(calculateProgress(timeLeft));
    onEarlyAccessTimeUpdate?.(timeLeft);
  };
  const responseRateFeature: FeatureItem | undefined = responseRate
    ? {
        iconVariant: 'IconComment',
        label: `Response Rate: ${responseRate}`,
      }
    : undefined;

  const responseTimeFeature: FeatureItem | undefined = responseTime
    ? {
        iconVariant: 'Bolt',
        label: `Response Time: ${responseTime}`,
      }
    : undefined;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="var(--spacing-3)"
      color="var(--text-primary)"
      className={className}
    >
      {heading && (
        <Heading size="2xs" fontWeight="bold">
          {heading}
        </Heading>
      )}

      <Box
        display="flex"
        flexDirection="column"
        gap="var(--spacing-4)"
        backgroundColor="var(--surface-neutral)"
        p="var(--spacing-4)"
        borderRadius="var(--radius-lg)"
      >
        <UserCard
          avatarSrc={avatarSrc}
          title={name}
          subtitle={
            <ProfileSubtitle
              hostingSince={hostingSince}
              featureReviewItem={featureReviewItem}
            />
          }
          showRating={false}
          isVerified={isVerified}
        />

        {hasTextContent(bio) && !!bio && (
          <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
            <Text fontWeight="bold">Bio</Text>
            <Text
              dangerouslySetInnerHTML={{
                __html: bio,
              }}
            />
          </Box>
        )}

        {(!!responseRateFeature || !!responseTimeFeature) && (
          <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
            <Text fontWeight="bold">Landowner Details</Text>
            <Box display="flex" flexDirection="column" gap="var(--spacing-2)">
              {!!responseRateFeature && (
                <FeatureListItem {...(responseRateFeature as FeatureItem)} />
              )}
              {!!responseTimeFeature && (
                <FeatureListItem {...(responseTimeFeature as FeatureItem)} />
              )}
            </Box>
          </Box>
        )}

        <Box alignSelf="flex-start">
          <Button
            variant="secondary"
            onClick={onMessageClick}
            disabled={isMessageDisabled}
          >
            {messageButtonText}
          </Button>
        </Box>

        {earlyAccessTimer && (
          <Box display="flex" flexDirection="column" gap="var(--spacing-3)">
            <ProgressBar progress={progress} />
            <Box
              display="flex"
              flexDirection="column"
              gap="var(--spacing-1)"
              textAlign="center"
            >
              <Text size="xs" textAlign="center">
                {earlyAccessTimer.labelText}
              </Text>
              <Text size="sm" textAlign="center">
                {earlyAccessTimer.descriptionText}
              </Text>
              <Box display="flex" justifyContent="center">
                <Timer
                  expirationTimestamp={earlyAccessTimer.expirationTimestamp}
                  onTimeUpdate={handleTimeUpdate}
                  onExpire={onEarlyAccessExpire}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LandownerProfile;
